/**
 * Date utility functions for LinCal
 * Centralized date handling to avoid timezone issues and code duplication
 */

/**
 * Convert a Date object to a timezone-agnostic date string (YYYY-MM-DD)
 * This avoids timezone issues when storing/parsing dates
 * @param {Date|string} date - Date object or ISO string
 * @returns {string|null} Date string in YYYY-MM-DD format
 */
export function toDateString(date) {
  if (!date) return null;
  
  if (typeof date === 'string') {
    // If already a string, extract the date part (handles both YYYY-MM-DD and ISO format)
    return date.substring(0, 10);
  }
  
  if (date instanceof Date) {
    // Format as YYYY-MM-DD using local time (not UTC)
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  
  return null;
}

/**
 * Parse a date string or Date object to a Date at midnight local time
 * @param {string|Date} input - Date string (YYYY-MM-DD) or Date object
 * @returns {Date} Date object at midnight local time
 */
export function parseDate(input) {
  if (!input) return new Date();
  if (input instanceof Date) return input;
  // Parse YYYY-MM-DD as local time
  const [year, month, day] = input.split('-').map(Number);
  return new Date(year, month - 1, day);
}

/**
 * Check if two dates are the same day
 * @param {Date|string} date1 - First date
 * @param {Date|string} date2 - Second date
 * @returns {boolean} True if dates are the same day
 */
export function isSameDay(date1, date2) {
  if (!date1 || !date2) return false;
  // Normalize to YYYY-MM-DD strings for comparison
  const str1 = toDateString(date1);
  const str2 = toDateString(date2);
  return str1 === str2;
}

/**
 * Get the day of week (0 = Monday, 6 = Sunday)
 * Converts from JavaScript's Sunday-first (0-6) to Monday-first (0-6)
 * @param {Date} date - Date object
 * @returns {number} Day of week (0 = Monday, 6 = Sunday)
 */
export function getDayOfWeekMondayFirst(date) {
  const day = date.getDay();
  return day === 0 ? 6 : day - 1;
}

/**
 * Check if a date is a weekend (Saturday or Sunday)
 * @param {Date|string} date - Date object or YYYY-MM-DD string
 * @returns {boolean} True if date is Saturday or Sunday
 */
export function isWeekend(date) {
  const dateObj = date instanceof Date ? date : parseDate(date);
  const day = dateObj.getDay();
  return day === 0 || day === 6; // Sunday or Saturday
}

/**
 * Calculate the number of days between two date strings (inclusive)
 * Uses local time to avoid timezone issues
 * @param {string} startDateStr - Start date in YYYY-MM-DD format
 * @param {string} endDateStr - End date in YYYY-MM-DD format
 * @returns {number} Number of days between dates (0 if same day, positive if end is after start)
 */
export function getDurationInDays(startDateStr, endDateStr) {
  if (!startDateStr || !endDateStr) return 0;
  
  // Parse as local time to avoid timezone shifts
  const [sy, sm, sd] = startDateStr.split('-').map(Number);
  const startDate = new Date(sy, sm - 1, sd);
  
  const [ey, em, ed] = endDateStr.split('-').map(Number);
  const endDate = new Date(ey, em - 1, ed);
  
  // Calculate difference in milliseconds and convert to days
  const diffMs = endDate - startDate;
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
  
  return diffDays;
}

/**
 * Add or subtract days from a date
 * @param {Date|string} date - Date object or YYYY-MM-DD string
 * @param {number} days - Number of days to add (negative to subtract)
 * @returns {Date} New Date object with days added
 */
export function addDays(date, days) {
  const dateObj = date instanceof Date ? new Date(date) : parseDate(date);
  dateObj.setDate(dateObj.getDate() + days);
  return dateObj;
}

/**
 * Calculate total days and workdays between two dates (inclusive)
 * Workdays exclude weekends (Saturday and Sunday)
 * @param {Date|string} start - Start date
 * @param {Date|string} end - End date
 * @returns {{totalDays: number, workDays: number}} Object with totalDays and workDays
 */
export function calculateDuration(start, end) {
  if (!start || !end) return { totalDays: 0, workDays: 0 };
  
  const startDate = start instanceof Date ? start : parseDate(start);
  const endDate = end instanceof Date ? end : parseDate(end);
  
  let totalDays = 0;
  let workDays = 0;
  
  const current = new Date(startDate);
  while (current <= endDate) {
    totalDays++;
    const dayOfWeek = current.getDay(); // 0 = Sunday, 6 = Saturday
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      workDays++;
    }
    current.setDate(current.getDate() + 1);
  }
  
  return { totalDays, workDays };
}

/**
 * Format a date as a localized date string
 * @param {string|Date} dateInput - Date to format (ISO string or Date object)
 * @param {string} [locale] - Optional locale (defaults to browser locale)
 * @returns {string} Formatted date string
 */
export function formatRelativeDate(dateInput, locale) {
  if (!dateInput) return '';
  
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  
  // Use localized date format
  return date.toLocaleDateString(locale, { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
}
